import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Paper, Typography } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import KanbanCard from "./KanbanCard";
import logger from "../../utils/logger";
import { cardSurface, BORDERS } from "../../theme/redesign";
import { i18n } from "../../translate/i18n";

// 0259 UX: Maqueta compacta + responsive (zoom, pantallas pequeñas)
const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "nowrap",
    height: "100%",
    padding: theme.spacing(1.5),
    gap: theme.spacing(1.5),
    overflowX: "auto",
    overflowY: "hidden",
    // Adaptar al zoom: minWidth en columnas fuerza scroll horizontal
    "-webkit-overflow-scrolling": "touch",
  },
  column: {
    ...cardSurface(theme),
    flex: "0 0 auto",
    width: 280,
    minWidth: 240,
    maxWidth: 320,
  },
  columnHeader: {
    padding: theme.spacing(1, 1.5),
    borderBottom: BORDERS.base(theme),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  columnTitle: {
    fontWeight: 600,
    fontSize: "0.85rem",
    lineHeight: 1.3,
  },
  columnCount: {
    color: "white",
    borderRadius: 999,
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.7rem",
    fontWeight: "bold",
    fontVariantNumeric: "tabular-nums",
  },
  cardsContainer: {
    padding: theme.spacing(0.75),
    maxHeight: "calc(100vh - 180px)",
    minHeight: 80,
    overflowY: "auto",
    overflowX: "hidden",
  },
}));

const KanbanBoard = ({ tickets, tags, onCardMove, onCardClick, agendaActive = false }) => {
  const classes = useStyles();
  const history = useHistory();
  const [localTickets, setLocalTickets] = useState(tickets);
  const [isMoving, setIsMoving] = useState(false);

  // ✅ Actualizar tickets locales cuando cambien los props
  React.useEffect(() => {
    setLocalTickets(tickets);
  }, [tickets]);

  // ✅ Filtrar solo etiquetas que tienen kanban activado (0258 C1: columnas por perfil)
  // eslint-disable-next-line eqeqeq
  const etiquetasKanban = tags.filter((tag) => tag.kanban == 1 || tag.kanban === true);

  // ✅ 0258 C2: Posicionar por kanbanProfileStage cuando hay cita, sino por tags
  // Comparación case-insensitive: "SESIÓN CONFIRMADA" coincide con "Sesión Confirmada" (DB)
  // "SESIÓN COMPLETA" y "SESIÓN COMPLETADA" se consideran equivalentes
  const matchKanbanStage = (tagName, stage) => {
    if (!tagName || !stage) return false;
    const t = String(tagName).trim().toLowerCase();
    const st = String(stage).trim().toLowerCase();
    if (t === st) return true;
    return t.includes("completa") && st.includes("completa");
  };

  const ticketPerteneceAColumna = (ticket, etiquetasKanbanList) => {
    if (ticket.kanbanProfileStage && typeof ticket.kanbanProfileStage === "string") {
      const match = etiquetasKanbanList.find(
        (t) => matchKanbanStage(t.name, ticket.kanbanProfileStage)
      );
      if (match) return true;
    }
    if (ticket.tags && Array.isArray(ticket.tags)) {
      // eslint-disable-next-line eqeqeq
      return ticket.tags.some((tag) => tag && (tag.kanban == 1 || tag.kanban === true));
    }
    return false;
  };

  const ticketsSinEtiquetas = localTickets.filter((ticket) => !ticketPerteneceAColumna(ticket, etiquetasKanban));
  
  // ✅ Etiquetas kanban procesadas
  
  // ✅ DEBUG DETALLADO: Verificar estructura de tickets antes del filtrado
  React.useEffect(() => {
    // ✅ CORREGIDO: Solo ejecutar una vez cuando se cargan los tickets inicialmente
    if (localTickets.length > 0 && localTickets !== tickets) {
      logger.dashboard.debug('🔍 [DEBUG] Estructura de tickets recibidos:', {
        totalTickets: localTickets.length,
        sampleTickets: localTickets.slice(0, 3).map(t => ({
          id: t.id,
          hasTags: !!t.tags,
          tagsType: typeof t.tags,
          tagsIsArray: Array.isArray(t.tags),
          tagsLength: t.tags?.length || 0,
          hasCompraRealizada: t.tags?.some(tag => tag.id === 4) || false
        }))
      });
    }
  }, [localTickets, tickets]);

  // ✅ 0258 C2: Asignar cada ticket a una sola columna. Prioridad: kanbanProfileStage > tags.
  const ticketsPorEtiqueta = {};
  etiquetasKanban.forEach((tag) => {
    ticketsPorEtiqueta[tag.id] = [];
  });
  localTickets.forEach((ticket) => {
    // Prioridad 1: kanbanProfileStage (cita vinculada) — comparación case-insensitive
    if (ticket.kanbanProfileStage && typeof ticket.kanbanProfileStage === "string") {
      const colTag = etiquetasKanban.find(
        (t) => matchKanbanStage(t.name, ticket.kanbanProfileStage)
      );
      if (colTag && ticketsPorEtiqueta[colTag.id]) {
        ticketsPorEtiqueta[colTag.id].push(ticket);
        return;
      }
    }
    // Prioridad 2: tags con kanban=1 (primer tag que coincida)
    if (ticket.tags && Array.isArray(ticket.tags)) {
      for (const t of etiquetasKanban) {
        const match = ticket.tags.some((tt) => tt && tt.id === t.id);
        if (match) {
          ticketsPorEtiqueta[t.id].push(ticket);
          return;
        }
      }
    }
    // Si no coincide con ninguna columna, va en ticketsSinEtiquetas (ABIERTOS)
  });


  // ✅ Debug para verificar etiquetas kanban y tickets
  React.useEffect(() => {
    // ✅ CORREGIDO: Solo ejecutar cuando cambien los datos reales, no en cada render
    if (localTickets.length > 0 && localTickets !== tickets) {
      logger.dashboard.debug('🔄 Kanban dinámico actualizado:', {
        totalTickets: localTickets.length,
        sinEtiquetas: ticketsSinEtiquetas.length,
        etiquetasKanban: etiquetasKanban.map(tag => ({
          id: tag.id,
          name: tag.name,
          kanban: tag.kanban,
          tickets: ticketsPorEtiqueta[tag.id]?.length || 0
        }))
      });
      
      // ✅ DEBUG EXTENDIDO: Verificar tickets sin etiquetas
      if (ticketsSinEtiquetas.length > 0) {
        logger.dashboard.debug('🔄 [KanbanBoard] Tickets ABIERTOS encontrados:', 
          ticketsSinEtiquetas.slice(0, 5).map(t => ({
            id: t.id,
            contactName: t.contact?.name,
            tags: t.tags?.length || 0,
            tagNames: t.tags?.map(tag => tag.name) || []
          }))
        );
      } else {
        logger.dashboard.warn('⚠️ [KanbanBoard] NO HAY TICKETS SIN ETIQUETAS - ABIERTOS estará vacío');
        
        // ✅ DEBUG: Verificar estructura de tickets recibidos
        if (localTickets.length > 0) {
          logger.dashboard.debug('🔄 [KanbanBoard] Estructura de tickets recibidos:', 
            localTickets.slice(0, 3).map(t => ({
              id: t.id,
              hasTags: !!t.tags,
              tagsLength: t.tags?.length || 0,
              tagsType: typeof t.tags,
              tagsIsArray: Array.isArray(t.tags),
              rawTags: t.tags
            }))
          );
        }
      }
    }
  }, [localTickets, tickets, etiquetasKanban, ticketsPorEtiqueta, ticketsSinEtiquetas]); // ✅ CORREGIDO: Dependencias completas

  // ✅ 0259 P5: Paleta maqueta (fallback cuando tag.color vacío)
  const PALETA_MAQUETA = ["#2196F3", "#FF9800", "#4CAF50", "#9E9E9E", "#F44336"]; // azul, naranja, verde, gris, rojo
  const getColorColumna = (tag, index) => {
    if (tag?.color && String(tag.color).trim()) return tag.color;
    return PALETA_MAQUETA[index % PALETA_MAQUETA.length];
  };

  const columns = [];
  if (!agendaActive) {
    columns.push({
      id: "abiertos",
      title: i18n.t("kanban.open"),
      tickets: ticketsSinEtiquetas,
      color: "#607D8B"
    });
  }
  etiquetasKanban.forEach((tag, index) => {
    columns.push({
      id: `tag-${tag.id}`,
      title: tag.name.toUpperCase(),
      tickets: ticketsPorEtiqueta[tag.id] || [],
      color: getColorColumna(tag, index)
    });
  });

  const handleCardClickInternal = (ticket) => {
    if (typeof onCardClick === "function") {
      onCardClick(ticket);
      return;
    }
    const ticketKey = ticket?.uuid || ticket?.id;
    if (!ticketKey) return;
    history.push(`/tickets/${ticketKey}`);
  };

  const handleCalendarClick = (e) => {
    e?.stopPropagation?.();
    history.push({ pathname: "/appointments", state: { tabIndex: 1 } });
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;
    
    if (source.droppableId === destination.droppableId) {
      // Mismo column, solo reordenar
      return;
    }

    // Diferente column, mover ticket o cita (0266: cardId puede ser "appointment-X" o "ticket-X")
    try {
      setIsMoving(true);
      const sourceLaneId = source.droppableId;
      const targetLaneId = destination.droppableId;

      // ✅ ACTUALIZACIÓN INMEDIATA LOCAL — buscar por cardId o id
      const ticketToMove = localTickets.find(
        t => String(t.cardId || t.id) === String(draggableId)
      );
      if (ticketToMove) {
        const isAppointmentCard = String(draggableId).startsWith('appointment-');
        const updatedTicket = { ...ticketToMove };

        if (targetLaneId.startsWith('tag-')) {
          const targetTagId = parseInt(targetLaneId.replace('tag-', ''), 10);
          const targetTag = etiquetasKanban.find(tag => tag.id === targetTagId);

          if (isAppointmentCard && targetTag) {
            // 0266: Card de cita — actualizar kanbanProfileStage (la columna viene del status de la cita)
            updatedTicket.kanbanProfileStage = targetTag.name;
            logger.dashboard.debug('🔄 Card cita: kanbanProfileStage →', targetTag.name);
          } else if (targetTag) {
            // Card de ticket — actualizar tags
            if (sourceLaneId.startsWith('tag-')) {
              const sourceTag = etiquetasKanban.find(tag => tag.id === parseInt(sourceLaneId.replace('tag-', ''), 10));
              if (sourceTag) {
                updatedTicket.tags = (updatedTicket.tags || []).filter(tag => tag.id !== sourceTag.id);
              }
            }
            const alreadyHasTag = (updatedTicket.tags || []).some(tag => tag.id === targetTag.id);
            if (!alreadyHasTag) {
              updatedTicket.tags = [...(updatedTicket.tags || []), targetTag];
            }
          }
        } else if (targetLaneId === "abiertos") {
          updatedTicket.kanbanProfileStage = null;
          updatedTicket.tags = (updatedTicket.tags || []).filter(
            (tag) => tag && !(tag.kanban == 1 || tag.kanban === true)
          );
        }

        setLocalTickets(prev =>
          prev.map(t =>
            String(t.cardId || t.id) === String(draggableId) ? updatedTicket : t
          )
        );
      }

      // Llamar a la función de movimiento (0266: cardId puede ser appointment-X o ticket-X)
      await onCardMove(draggableId, sourceLaneId, targetLaneId);
      
      // ✅ Confirmación visual de movimiento exitoso
      logger.dashboard.debug('✅ Movimiento completado exitosamente');
      
    } catch (error) {
      logger.dashboard.error("Error moving card:", error);
      // Si hay error, revertir el cambio local
      setLocalTickets(tickets);
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className={classes.root}>
          {columns.map(column => (
            <Paper key={column.id} className={classes.column} elevation={1}>
              <div
                className={classes.columnHeader}
                style={{ borderLeft: `4px solid ${column.color}` }}
              >
                <Typography className={classes.columnTitle} style={{ color: column.color }}>
                  {column.title}
                </Typography>
                <div
                  className={classes.columnCount}
                  style={{ backgroundColor: column.color }}
                >
                  {column.tickets.length}
                </div>
              </div>
              
              <Droppable droppableId={column.id}>
                {(provided, snapshot) => (
                  <div 
                    className={classes.cardsContainer}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {column.tickets.map((ticket, index) => (
                      <Draggable
                        key={(ticket.cardId || ticket.id).toString()}
                        draggableId={(ticket.cardId || ticket.id).toString()}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <KanbanCard
                              ticket={ticket}
                              isMoving={isMoving}
                              isDragging={snapshot.isDragging}
                              onCardClick={handleCardClickInternal}
                              onCalendarClick={handleCalendarClick}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </Paper>
          ))}
        </div>
      </DragDropContext>
    </>
  );
};

export default KanbanBoard; 
